import { gozokiApiWithAuth } from '@gozoki/api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { api } from '~/lib/tuyau'
import { Loader } from 'lucide-react'
import React, { useState } from 'react'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'

type VisionStatus = 'ok' | 'more' | 'less' | 'obstacle' | 'unverified'

interface VisionCheck {
  id: number
  cartId: number
  expectedCount: number
  visualCount: number | null
  delta: number | null
  status: VisionStatus
  isBag: boolean
  confident: boolean
  capturesCount: number
  durationMs: number | null
  geminiMs: number | null
  geminiModel: string | null
  isCorrect: boolean | null
  error: string | null
  createdAt: string | null
}

interface ChecksResponse {
  data: VisionCheck[]
  pagination: { total: number; limit: number; offset: number }
}

interface StatsResponse {
  total: number
  byStatus: Record<string, number>
  unverifiedRate: number
  latencyMs: { p50: number | null; p95: number | null; avg: number | null; max: number | null }
  correctness: { correct: number; incorrect: number; unreviewed: number }
}

const PAGE_SIZE = 50

const STATUS_LABEL: Record<VisionStatus, string> = {
  ok: 'OK',
  more: 'Plus (vu>RFID)',
  less: 'Moins (vu<RFID)',
  obstacle: 'Sac/obstacle',
  unverified: 'Non vérifié',
}

const getDeltaClassName = (delta: number): string => {
  if (delta === 0) return 'font-bold text-green-600'
  if (delta > 0) return 'font-bold text-orange-600'
  return 'font-bold text-gray-500'
}

function statusBadge(status: VisionStatus) {
  if (status === 'ok') return <Badge className="bg-green-600">OK</Badge>
  if (status === 'more') return <Badge className="bg-orange-600">Plus</Badge>
  if (status === 'less') return <Badge variant="secondary">Moins</Badge>
  if (status === 'obstacle') return <Badge className="bg-red-600">Sac</Badge>
  return <Badge variant="outline">Non vérifié</Badge>
}

export const Route = createFileRoute('/_authenticated/cart-vision-checks')({
  component: CartVisionChecksPage,
})

function CartVisionChecksPage() {
  const [filter, setFilter] = useState('all')
  const [sortBy, setSortBy] = useState('created')
  const [sortDir, setSortDir] = useState('desc')
  const [page, setPage] = useState(0)

  const { data: stats } = useQuery({
    queryKey: ['cart-vision-stats'],
    queryFn: async () => {
      const res = await gozokiApiWithAuth.get<StatsResponse>('/cart-vision-checks/stats')
      return res.data
    },
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['cart-vision-checks', filter, sortBy, sortDir, page],
    queryFn: async () => {
      const res = await gozokiApiWithAuth.get<ChecksResponse>('/cart-vision-checks', {
        params: { filter, sortBy, sortDir, limit: PAGE_SIZE, offset: page * PAGE_SIZE },
      })
      return res.data
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader className="animate-spin" />
      </div>
    )
  }
  if (error) {
    return <div className="p-4 text-red-600">Erreur : {String(error)}</div>
  }

  const total = data?.pagination.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div className="mx-auto max-w-7xl p-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Vérification visuelle (shadow mode)</h1>
        <div className="text-sm text-gray-500">{total} événement(s)</div>
      </div>

      {stats && (
        <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard title="Total" value={stats.total} />
          <StatCard
            title="Latence p50 / p95"
            value={`${stats.latencyMs.p50 ?? '—'} / ${stats.latencyMs.p95 ?? '—'} ms`}
          />
          <StatCard
            title="Divergences (plus/moins)"
            value={(stats.byStatus.more ?? 0) + (stats.byStatus.less ?? 0)}
            hint={`dont ${stats.byStatus.more ?? 0} « vu>RFID »`}
          />
          <StatCard
            title="Sacs / Non vérifiés"
            value={`${stats.byStatus.obstacle ?? 0} / ${stats.byStatus.unverified ?? 0}`}
            hint={`${Math.round(stats.unverifiedRate * 100)}% non vérifiés`}
          />
          <StatCard
            title="Jugés corrects"
            value={stats.correctness.correct}
            hint={`${stats.correctness.incorrect} faux · ${stats.correctness.unreviewed} à juger`}
          />
        </div>
      )}

      <div className="mb-6 flex gap-4">
        <Select
          value={filter}
          onValueChange={(v: string | null) => {
            setFilter(v ?? 'all')
            setPage(0)
          }}
        >
          <SelectTrigger className="w-56">
            <SelectValue placeholder="Filtre" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tout</SelectItem>
            <SelectItem value="divergence">Divergences (plus/moins)</SelectItem>
            <SelectItem value="loss">Perte (vu&gt;RFID)</SelectItem>
            <SelectItem value="bag">Sac / obstacle</SelectItem>
            <SelectItem value="ok">OK</SelectItem>
            <SelectItem value="unverified">Non vérifié</SelectItem>
          </SelectContent>
        </Select>

        <Select value={sortBy} onValueChange={(v: string | null) => setSortBy(v ?? 'created')}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Trier par" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="created">Date</SelectItem>
            <SelectItem value="delta">Delta (écart)</SelectItem>
            <SelectItem value="duration">Durée du call</SelectItem>
          </SelectContent>
        </Select>

        <Button variant="outline" onClick={() => setSortDir(sortDir === 'desc' ? 'asc' : 'desc')}>
          {sortDir === 'desc' ? '↓ DESC' : '↑ ASC'}
        </Button>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Cart ID</TableHead>
              <TableHead className="text-center">RFID</TableHead>
              <TableHead className="text-center">IA</TableHead>
              <TableHead className="text-center">Δ</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead className="text-center">Durée</TableHead>
              <TableHead className="text-center">Pertinent ?</TableHead>
              <TableHead className="text-right">Photos</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data?.data.map((c) => (
              <TableRow key={c.cartId}>
                <TableCell className="font-mono">{c.cartId}</TableCell>
                <TableCell className="text-center">{c.expectedCount}</TableCell>
                <TableCell className="text-center">{c.visualCount ?? '—'}</TableCell>
                <TableCell className="text-center">
                  {c.delta === null ? (
                    '—'
                  ) : (
                    <span className={getDeltaClassName(c.delta)}>
                      {c.delta > 0 ? '+' : ''}
                      {c.delta}
                    </span>
                  )}
                </TableCell>
                <TableCell title={STATUS_LABEL[c.status]}>{statusBadge(c.status)}</TableCell>
                <TableCell className="text-center text-sm">
                  {c.durationMs === null ? '—' : `${c.durationMs} ms`}
                </TableCell>
                <TableCell className="text-center">
                  <CorrectnessButtons check={c} />
                </TableCell>
                <TableCell className="text-right">
                  <PhotosDialog check={c} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="mt-6 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Page {page + 1} / {totalPages}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" disabled={page === 0} onClick={() => setPage(page - 1)}>
            ← Précédent
          </Button>
          <Button
            variant="outline"
            disabled={page >= totalPages - 1}
            onClick={() => setPage(page + 1)}
          >
            Suivant →
          </Button>
        </div>
      </div>
    </div>
  )
}

function StatCard({
  title,
  value,
  hint,
}: {
  title: string
  value: string | number
  hint?: string
}) {
  return (
    <div className="rounded-lg border p-3">
      <div className="text-xs text-gray-500">{title}</div>
      <div className="text-xl font-bold">{value}</div>
      {hint && <div className="text-xs text-gray-400">{hint}</div>}
    </div>
  )
}

function CorrectnessButtons({ check }: { check: VisionCheck }) {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (isCorrect: boolean | null) => {
      await gozokiApiWithAuth.patch(`/cart-vision-checks/${check.id}`, { isCorrect })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cart-vision-checks'] })
      queryClient.invalidateQueries({ queryKey: ['cart-vision-stats'] })
    },
  })

  return (
    <div className="flex justify-center gap-1">
      <Button
        size="sm"
        variant={check.isCorrect === true ? 'default' : 'outline'}
        className={check.isCorrect === true ? 'bg-green-600' : ''}
        disabled={mutation.isPending}
        onClick={() => mutation.mutate(check.isCorrect === true ? null : true)}
      >
        ✓
      </Button>
      <Button
        size="sm"
        variant={check.isCorrect === false ? 'default' : 'outline'}
        className={check.isCorrect === false ? 'bg-red-600' : ''}
        disabled={mutation.isPending}
        onClick={() => mutation.mutate(check.isCorrect === false ? null : false)}
      >
        ✗
      </Button>
    </div>
  )
}

function PhotosDialog({ check }: { check: VisionCheck }) {
  const [open, setOpen] = useState(false)
  const [zoomUrl, setZoomUrl] = useState<string | null>(null)

  const {
    data,
    isLoading,
    error: imagesError,
  } = useQuery(
    api.carts.getImages.queryOptions({ params: { id: check.cartId } }, { enabled: open }),
  )
  const images = data?.images

  let imagesContent: React.ReactNode = undefined
  if (isLoading) {
    imagesContent = <Loader className="animate-spin" />
  } else if (imagesError) {
    imagesContent = (
      <div className="text-sm text-red-600">
        Impossible de charger les photos (droits insuffisants ?)
      </div>
    )
  } else if (images && images.length > 0) {
    imagesContent = (
      <div className="grid grid-cols-3 gap-2">
        {images.map((img) => (
          <img
            key={img.key}
            src={img.url}
            alt={img.key}
            onClick={() => setZoomUrl(img.url)}
            className="aspect-square w-full cursor-zoom-in rounded bg-black object-contain transition hover:opacity-80"
          />
        ))}
      </div>
    )
  } else {
    imagesContent = <div className="text-gray-500">Aucune photo</div>
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(v) => {
          setOpen(v)
          if (!v) setZoomUrl(null)
        }}
      >
        <DialogTrigger
          render={
            <Button variant="ghost" size="sm">
              Voir
            </Button>
          }
        />
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Panier {check.cartId}</DialogTitle>
            <DialogDescription>
              RFID {check.expectedCount} — IA {check.visualCount ?? '?'} (
              {STATUS_LABEL[check.status]}){check.geminiModel ? ` — ${check.geminiModel}` : ''}
              {check.geminiMs !== null ? ` — Gemini ${check.geminiMs} ms` : ''}
            </DialogDescription>
          </DialogHeader>

          {check.error && <div className="text-sm text-red-600">Erreur : {check.error}</div>}

          {imagesContent}
        </DialogContent>
      </Dialog>
      {zoomUrl && (
        <div
          className="fixed inset-0 z-[60] flex cursor-zoom-out items-center justify-center bg-black/90 p-4"
          onClick={() => setZoomUrl(null)}
        >
          <img src={zoomUrl} alt="agrandi" className="max-h-full max-w-full object-contain" />
        </div>
      )}
    </>
  )
}
